// src/components/settings/BillingSettings.js
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config'; 
import { CreditCard, Clock, ChevronRight, CheckCircle, AlertTriangle, Users } from 'lucide-react';
import { STRIPE_PRODUCTS } from '../../config/stripeProducts';

// Detect Canadian timezone for CAD pricing (same logic as PricingTable)
const CANADIAN_TIMEZONES = new Set([
    'America/Toronto','America/Vancouver','America/Edmonton','America/Winnipeg',
    'America/Halifax','America/St_Johns','America/Regina','America/Whitehorse',
    'America/Yellowknife','America/Iqaluit','America/Moncton','America/Glace_Bay',
]);
const isCanadian = CANADIAN_TIMEZONES.has(Intl.DateTimeFormat().resolvedOptions().timeZone);

// [trial-countdown] Pure, testable. The "ends today" / "has ended" boundary is
// decided by LOCAL CALENDAR DAY: a trial ending any time TODAY reads "ends today",
// and an earlier day reads "has ended" (never a negative number). For a FUTURE
// calendar day we count the remaining time and round UP with Math.ceil, so any
// time still on the clock never reads as fewer days than it is — deliberate:
// 1.01 days -> "2 days left", 0.99 days -> "1 day left", exactly at expiry -> "ends today".
function getTrialCountdown(trialEndDate, now) {
    if (!trialEndDate) return null;
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
    const endDay = startOfDay(trialEndDate).getTime();
    const nowDay = startOfDay(now).getTime();
    if (endDay < nowDay) return 'Your free trial has ended';
    if (endDay === nowDay) return 'Your free trial ends today';
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil((trialEndDate - now) / MS_PER_DAY);
    return daysLeft === 1
        ? '1 day left in your free trial'
        : `${daysLeft} days left in your free trial`;
}

const BillingSettings = ({ currentUserProfile }) => {
    const [isPortalLoading, setIsPortalLoading] = useState(false);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');

    const status = currentUserProfile?.subscriptionStatus || 'unknown';
    const planTier = currentUserProfile?.planTier || 'solo';

    // trialEnd is a Firestore Timestamp (written server-side by the webhook/backfill);
    // handle both a Timestamp and a raw date defensively. Only rendered when trialing.
    const trialEndDate = currentUserProfile?.trialEnd
        ? (currentUserProfile.trialEnd.toDate ? currentUserProfile.trialEnd.toDate() : new Date(currentUserProfile.trialEnd))
        : null;
    const trialEndLabel = trialEndDate
        ? trialEndDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;

    // [trial-countdown] Only compute/show a countdown while genuinely trialing.
    const trialCountdown = status === 'trialing' ? getTrialCountdown(trialEndDate, new Date()) : null;

    // Inactive = trial ended or canceled — needs a fresh checkout, not the portal
    const isInactive = ['canceled', 'paused', 'incomplete_expired', 'unpaid'].includes(status);

    // Pre-select their previous plan, defaulting to solo
    const [selectedPlan, setSelectedPlan] = useState(
        Object.keys(STRIPE_PRODUCTS).includes(planTier?.toLowerCase()) 
            ? planTier.toLowerCase() 
            : 'solo'
    );
    const [billing, setBilling] = useState('monthly');

    const handleResubscribe = async () => {
        setCheckoutError('');
        const product = STRIPE_PRODUCTS[selectedPlan];
        if (!product) return;
        const priceKey = isCanadian 
            ? (billing === 'annual' ? 'annual_cad' : 'monthly_cad')
            : (billing === 'annual' ? 'annual' : 'monthly');
        const priceId = product.prices[priceKey];
        setIsCheckoutLoading(true);
        try {
            const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
            const { data } = await createCheckoutSession({
                priceId,
                // trialonce-6b3f9d24 — this used to send skipTrial:true, which
                // createCheckoutSession never read: the trial was applied anyway and every
                // lapsed customer who resubscribed got another free month. Trial
                // eligibility is now decided server-side from the account's own Stripe
                // history, so there is no flag to send. Do not add one back — a returning
                // customer can also reach checkout from /pricing, which sends nothing.
                successUrl: `${window.location.origin}/home`,
                cancelUrl: `${window.location.origin}/settings?tab=billing`,
            });
            if (data.url) window.location.href = data.url;
        } catch (err) {
            console.error('Resubscribe error:', err);
            setCheckoutError('Unable to start checkout. Please try again.');
            setIsCheckoutLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        // Safety Check: We need the Stripe ID to open the portal.
        // If this is missing, the server will crash (500 Error).
        if (!currentUserProfile?.stripeCustomerId) {
            console.error("Missing stripeCustomerId:", currentUserProfile);
            alert("Error: No Billing ID found for this account. Please contact support.");
            return;
        }

        setIsPortalLoading(true);
        try {
            const createPortalSession = httpsCallable(functions, 'createPortalSession');
            const { data } = await createPortalSession({ 
                returnUrl: window.location.href 
            });
            if (data.url) window.location.href = data.url;
        } catch (error) {
            console.error("Failed to load portal:", error);
            alert("Unable to load the billing portal. Please refresh and try again.");
            setIsPortalLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <CreditCard className="text-blue-600" /> Billing & Plan
            </h2>

            {/* PLAN CARD */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">Current Subscription</h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                                status === 'active' ? 'bg-green-100 text-green-800' : 
                                status === 'canceled' ? 'bg-red-100 text-red-800' : 
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                                {planTier}
                            </span>
                        </div>
                        <div className="text-gray-500 text-sm flex items-start gap-2 max-w-md">
                            <Clock size={16} className="shrink-0 mt-0.5 text-blue-500" />
                            <span>Manage your billing details, download invoices, and update your payment method via the secure portal.</span>
                        </div>
                    </div>

                    {!isInactive && (
                        <button
                            onClick={handleManageSubscription}
                            disabled={isPortalLoading}
                            className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-black transition-all shadow-md active:scale-95 disabled:opacity-70"
                        >
                            {isPortalLoading ? 'Loading Portal...' : (
                                <>
                                    Manage Plan <ChevronRight size={16} />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* RESUBSCRIBE PLAN SELECTOR — shown only when inactive */}
            {isInactive && (
                <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">Choose your plan</h3>
                    <p className="text-sm text-gray-500 mb-5">Your previous plan is pre-selected. You can change it before subscribing.</p>

                    {/* Billing toggle */}
                    <div className="flex items-center gap-3 mb-5">
                        <button
                            onClick={() => setBilling('monthly')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >Monthly</button>
                        <button
                            onClick={() => setBilling('annual')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${billing === 'annual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >Annual <span className="text-green-600 font-bold">−20%</span></button>
                        {isCanadian && <span className="text-xs text-blue-600 font-semibold">🍁 CAD pricing</span>}
                    </div>

                    {/* Plan cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                        {Object.entries(STRIPE_PRODUCTS).map(([key, product]) => {
                            const priceKey = isCanadian
                                ? (billing === 'annual' ? 'annual_cad' : 'monthly_cad')
                                : (billing === 'annual' ? 'annual' : 'monthly');
                            const displayKey = isCanadian
                                ? (billing === 'annual' ? 'annual_cad' : 'monthly_cad')
                                : (billing === 'annual' ? 'annual_usd' : 'monthly_usd');
                            const price = product.displayPrices[displayKey];
                            const isSelected = selectedPlan === key;
                            return (
                                <div
                                    key={key}
                                    onClick={() => setSelectedPlan(key)}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                        isSelected 
                                            ? 'border-blue-600 bg-blue-50 shadow-md' 
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <p className="font-bold text-gray-800">{product.name.replace('Spatchy ', '')}</p>
                                        {isSelected && <CheckCircle size={16} className="text-blue-600 shrink-0" />}
                                    </div>
                                    <p className="text-2xl font-extrabold text-gray-900">
                                        {isCanadian ? 'CA' : 'US'}${billing === 'annual' ? Math.round(price / 12) : price}
                                        <span className="text-sm font-normal text-gray-500">/mo</span>
                                    </p>
                                    {billing === 'annual' && (
                                        <p className="text-xs text-green-600 font-semibold mt-0.5">Billed ${price}/yr</p>
                                    )}
                                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                        <Users size={11} />
                                        <span>Up to {product.userLimit} user{product.userLimit > 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {checkoutError && (
                        <p className="text-sm text-red-600 mb-3 flex items-center gap-2">
                            <AlertTriangle size={14} /> {checkoutError}
                        </p>
                    )}

                    <button
                        onClick={handleResubscribe}
                        disabled={isCheckoutLoading}
                        className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-md active:scale-95 disabled:opacity-70"
                    >
                        {isCheckoutLoading ? 'Starting checkout...' : `Subscribe to ${STRIPE_PRODUCTS[selectedPlan]?.name} →`}
                    </button>
                    <p className="text-xs text-gray-400 text-center mt-2">Secure checkout powered by Stripe. You can apply a promo code at checkout.</p>
                </div>
            )}

            {/* FEATURES SUMMARY (Mobile Optimized Stack) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                
                {/* 1. Seats */}
                <div className="p-5 bg-blue-50/50 rounded-xl border border-blue-100">
                    <h4 className="font-semibold text-gray-600 mb-2 text-sm uppercase tracking-wide">Included Seats</h4>
                    <p className="text-3xl font-extrabold text-blue-700">
                        {planTier === 'scale' ? '20' : planTier === 'growth' ? '10' : planTier === 'starter' ? '5' : '1'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Active Users Allowed</p>
                </div>

                {/* 2. Status */}
                <div className={`p-5 rounded-xl border ${
                    ['active', 'trialing'].includes(status) ? 'bg-green-50/50 border-green-100' :
                    'bg-red-50/50 border-red-100'
                }`}>
                    <h4 className="font-semibold text-gray-600 mb-2 text-sm uppercase tracking-wide">Account Status</h4>
                    <div className="flex items-center gap-2">
                        {['active', 'trialing'].includes(status)
                            ? <CheckCircle size={20} className="text-green-600"/>
                            : <AlertTriangle size={20} className="text-red-600"/>
                        }
                        <p className={`text-lg font-bold ${['active', 'trialing'].includes(status) ? 'text-green-800' : 'text-red-800'}`}>
                            {status === 'past_due'           ? 'Payment Failed' :
                             status === 'trialing'           ? (trialEndLabel ? `Free Trial — ends ${trialEndLabel}` : 'Free Trial') :
                             status === 'canceled'           ? 'Canceled' :
                             status === 'unpaid'             ? 'Subscription Ended' :
                             status === 'incomplete_expired' ? 'Subscription Ended' :
                             status === 'incomplete'         ? 'Payment Incomplete' :
                             status === 'paused'             ? 'Trial Ended' :
                             <span className="capitalize">{status}</span>}
                        </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{/* [trial-countdown] */}{trialCountdown || 'Current Standing'}</p>
                </div>

                {/* 3. Invoice History Link */}
                <div 
                    onClick={handleManageSubscription}
                    className="p-5 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-white hover:border-blue-300 hover:shadow-md transition-all group"
                >
                    <h4 className="font-semibold text-gray-600 mb-2 text-sm uppercase tracking-wide group-hover:text-blue-600 transition-colors">Invoice History</h4>
                    <p className="text-lg font-bold text-gray-900 flex items-center gap-2 group-hover:text-blue-700 transition-colors">
                        View Invoices <ChevronRight size={18} className="opacity-50 group-hover:translate-x-1 transition-transform"/>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Powered by Stripe</p>
                </div>
            </div>
        </div>
    );
};

export default BillingSettings;